import { type Upload, uploadManager } from '@jwplayer/companion-gateway';
import { useMount } from '@jwplayer/fe-common/module/react/hooks';
import { useEffect, useState } from 'react';

export type UploadType = 'media' | 'image' | 'original' | 'text_track';

export const UPLOAD_TYPE_READABLE_NAME = {
  media: 'media',
  image: 'image',
  original: 'original',
  text_track: 'text track',
};

export function useAllUploadsState() {
  /**
   * ⚠️ This state is only used to trigger a re-render in React. The source of truth is always the UploadManager!
   *
   * Any modifications in the UploadManager (e.g. removing an upload), will automatically
   * be reflected here, so direct modifications of the React state are not needed/allowed.
   */
  const [uploads, setUploads] = useState(uploadManager.getState());

  /**
   * Subscribe to changes in the UploadManager, and re-render on every state change
   */
  useEffect(() => {
    // Update state when our upload manager changes state. This triggers a re-render.
    const unsub = uploadManager.subscribe(
      (s) => s,
      (nextState) => {
        setUploads(nextState);
      },
    );

    return () => {
      unsub();
    };
  }, []);

  return uploads;
}

/**
 * Subscribe to changes in an Upload's state, and re-render on every state change
 */
export const useUploadState = (upload: Upload) => {
  const [uploadState, setUploadState] = useState(upload.getState());

  useMount(() => {
    const unsub = upload.subscribe(
      (s) => s,
      (uploadState) => {
        setUploadState(uploadState);
      },
    );

    return () => {
      unsub();
    };
  });

  return uploadState;
};

export const pluralize = (uploadsCount: number, word: string) => {
  if (uploadsCount > 1) return word + 's';
  return word;
};

export const sortUploads = (a: Upload, b: Upload) => {
  const firstUploadState = a.getState().state;
  const secondUploadState = b.getState().state;

  if (firstUploadState === 'failed' && secondUploadState !== 'failed') {
    return 1;
  }
  if (firstUploadState !== 'failed' && secondUploadState === 'failed') {
    return -1;
  }
  return 0;
};
